/**
 * Helpers for compliance documents (terms / privacy / DPA).
 *
 * Source of truth: `GET /api/v1/platform/config/current-versions` on the
 * runtime. Use the React Query hook below to read the live values; the
 * runtime caches them server-side (~30s) so this is hot-path safe.
 */
import { useQuery } from '@tanstack/react-query';
import { platformConfigService, type PlatformVersions } from '@/services/platform/config';

export type ConsentDoc = 'terms' | 'privacy' | 'dpa';

export const DOC_LABELS: Record<ConsentDoc, string> = {
  terms: 'Terms of Service',
  privacy: 'Privacy Policy',
  dpa: 'Data Processing Agreement',
};

/**
 * Live current-versions read. Cached client-side for 60s so multiple
 * components in the same render cycle share one network call.
 */
export function useCurrentVersions() {
  return useQuery<PlatformVersions>({
    queryKey: ['platform', 'config', 'current-versions'],
    queryFn: () => platformConfigService.currentVersions(),
    staleTime: 60_000,
    refetchOnWindowFocus: false,
  });
}

/** Maps a doc kind to its current version from the live versions blob. */
export function currentVersionFor(doc: ConsentDoc, versions?: PlatformVersions): string | undefined {
  if (!versions) return undefined;
  if (doc === 'terms') return versions.termsVersion;
  if (doc === 'privacy') return versions.privacyVersion;
  return versions.dpaVersion;
}
